// ABOUTME: Past chats: the owner's chat with an agent before its last New chat, one entry per
// day (newest first); opening one shows that day's messages, read-only.
import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { ChevronLeft, Loader2 } from 'lucide-react'
import { getPastChat, listPastChats, type PastChat } from '@/api/agent'
import { Button } from '@/components/ui/button'
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import { cn } from '@/lib/utils'
import { MarkdownContent } from './MarkdownContent'

function dayLabel(day: string): string {
  return new Date(`${day}T12:00:00`).toLocaleDateString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: day.slice(0, 4) === String(new Date().getFullYear()) ? undefined : 'numeric',
  })
}

function Loading() {
  return (
    <div className="text-muted-foreground flex justify-center py-8">
      <Loader2 className="size-4 animate-spin" />
    </div>
  )
}

function DayList({ roleId, onOpen }: { roleId?: string; onOpen: (chat: PastChat) => void }) {
  const chats = useQuery({ queryKey: ['past-chats', roleId], queryFn: () => listPastChats(roleId) })
  if (chats.isPending) return <Loading />
  if (chats.isError) return <p className="text-destructive p-4 text-sm">{chats.error.message}</p>
  if (chats.data.length === 0) {
    return (
      <p className="text-muted-foreground p-4 text-sm" data-testid="past-chats-empty">
        No past chats yet. When you start a new chat, the one before it is kept here.
      </p>
    )
  }
  return (
    <ul className="divide-border divide-y" data-testid="past-chats-list">
      {chats.data.map((chat) => (
        <li key={chat.day}>
          <button
            type="button"
            className="hover:bg-muted/60 min-h-14 w-full px-4 py-3 text-left"
            onClick={() => onOpen(chat)}
            data-testid="past-chat-item"
          >
            <p className="text-muted-foreground text-xs">
              {dayLabel(chat.day)} · {chat.message_count} messages
            </p>
            <p className="truncate text-sm">{chat.title}</p>
          </button>
        </li>
      ))}
    </ul>
  )
}

function DayMessages({ roleId, chat }: { roleId?: string; chat: PastChat }) {
  const messages = useQuery({
    queryKey: ['past-chat', roleId, chat.day],
    queryFn: () => getPastChat(chat.day, roleId),
  })
  if (messages.isPending) return <Loading />
  if (messages.isError) {
    return <p className="text-destructive p-4 text-sm">{messages.error.message}</p>
  }
  return (
    <div className="space-y-3 p-4" data-testid="past-chat-messages">
      {messages.data.map((m) => (
        <div
          key={m.id}
          className={cn(
            'max-w-[85%] rounded-2xl px-3 py-2 text-sm',
            m.direction === 'inbound' ? 'bg-primary text-primary-foreground ml-auto' : 'bg-muted'
          )}
        >
          <MarkdownContent content={m.content} />
          <p className="mt-1 text-[0.6875rem] opacity-70">
            {new Date(m.created_at).toLocaleTimeString(undefined, {
              hour: 'numeric',
              minute: '2-digit',
            })}
          </p>
        </div>
      ))}
    </div>
  )
}

export function PastChatsSheet({
  open,
  onOpenChange,
  roleId,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** A team member's thread; Marketing's when absent. */
  roleId?: string
}) {
  const [chat, setChat] = useState<PastChat | null>(null)
  return (
    <Sheet
      open={open}
      onOpenChange={(next) => {
        if (!next) setChat(null)
        onOpenChange(next)
      }}
    >
      <SheetContent className="flex w-full flex-col gap-0 p-0 sm:max-w-md" data-testid="past-chats">
        <SheetHeader className="border-border/60 flex-row items-center gap-2 border-b">
          {chat && (
            <Button
              variant="ghost"
              size="icon-touch"
              onClick={() => setChat(null)}
              aria-label="Back to past chats"
            >
              <ChevronLeft className="size-5" />
            </Button>
          )}
          <SheetTitle>{chat ? dayLabel(chat.day) : 'Past chats'}</SheetTitle>
          <SheetDescription className="sr-only">
            Your earlier chats, kept when you start a new one.
          </SheetDescription>
        </SheetHeader>
        <div className="min-h-0 flex-1 overflow-y-auto">
          {chat ? (
            <DayMessages roleId={roleId} chat={chat} />
          ) : (
            <DayList roleId={roleId} onOpen={setChat} />
          )}
        </div>
      </SheetContent>
    </Sheet>
  )
}
